// Core domain types for Ophth POS.
// Single source of truth — shared by frontend, types-checked, used everywhere.

export type UserRole = 'owner' | 'cashier';

export interface AppUser {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
  activeEventId?: string;
  createdAt: number; // epoch ms
}

// --- Events ---
/**
 * `archived` and `void` both hide an event from the working list; the
 * difference is what they claim. Archived says "real, just not current" and is
 * reversible. Void says "should never have existed" and the database refuses it
 * once anything has been sold, because an invoice keeps naming its event.
 *
 * Only `active` sells. create_transaction() and create_return() test for that
 * one state rather than listing the bad ones, so a state added later is refused
 * by default.
 */
export type EventStatus = 'active' | 'closed' | 'archived' | 'void';

export interface AppEvent {
  id: string;
  name: string;
  code: string; // short id, e.g. "EVT-2026-A"
  status: EventStatus;
  location?: string;
  /** Palette token (see lib/eventColors.ts). Undefined renders neutral. */
  color?: string;
  startAt: number;
  endAt?: number;
  totalSales: number;
  totalTransactions: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

// --- Products ---
// Phase 2 redesign: Products are pure catalog entries. There is no global
// stock count on the product. Stock is tracked per-event via
// EventStockAllocation (see below). The actual warehouse inventory lives
// in a separate system.

export interface Product {
  id: string;
  sku: string;
  name: string;
  price: number; // IDR, integer
  costPrice?: number;
  /** Default packaging label for new stock of this product. */
  unit?: string;
  /** As the supplier's price list writes it. Undefined = not recorded. */
  brand?: string;
  /**
   * Pointer to a `Category.id` from the global `categories` collection.
   * Optional — products without a category are allowed (they show up
   * under "Tanpa kategori" in the POS grid). For backward compat, this
   * field may also be a free-form string for legacy products created
   * before the Categories collection existed; the UI merges both views.
   */
  categoryId?: string;
  imageUrl?: string;
  isActive: boolean;
  description?: string;
  createdAt: number;
  updatedAt: number;
}

// --- Categories (global, shared across all events) ---
// Owned by the admin. Products reference categories by id. The id
// string is treated opaquely by the rest of the app — we display
// `Category.name` everywhere. Renaming a category is therefore safe
// (it doesn't break product references).
export interface Category {
  id: string;
  name: string;
  /** Optional short description shown in the inventory manager. */
  description?: string;
  /** Sort order — lower comes first. Stable across reorders. */
  sortOrder: number;
  /** Soft-delete flag — hidden from POS but kept for transaction history. */
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

// --- Event Stock Allocations (per-event stock tracking) ---
// Each event tracks its own stock allocations. When a transaction is
// created (or fulfilled), the qtySold counter is incremented against
// the allocation. When a customer return happens, qtySold is decremented
// (the item is back at the event, not at the warehouse). When the event
// closes, the user records qtyReturnedToGudang per product; the delta
// is the loss/theft.

export interface EventStockAllocation {
  /**
   * Surrogate key. Until lots existed an allocation was identified by
   * (eventId, productId); now a product can hold several batches in one event,
   * so this is what the POS and returns address.
   */
  id: string;
  productId: string;
  productSku: string;
  productName: string;
  productPrice: number; // snapshot at allocation time
  qtyIn: number; // how many units were brought to the event
  qtySold: number; // incremented on sale; decremented on customer return
  qtyReturnedToGudang: number; // recorded at event close (default 0)
  /** Batch identifier as printed on the packaging. '' when none was recorded. */
  lotNumber: string;
  /** ms epoch, undefined when the product does not expire. */
  expiryDate?: number;
  /** Packaging label only — pcs, box, botol. No conversion between them. */
  unit: string;
  /** Order-form reference the batch was brought in under. '' when none. */
  orderForm: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  // Optional: locked after event closes
  closedAt?: number;
  closedBy?: string;
}

// --- Customers ---
export interface Customer {
  id: string;
  name: string;
  // `null` represents "field not set" round-tripped from Firestore; `undefined`
  // is reserved for "field not loaded yet" in memory. Callers should treat
  // both the same (i.e. as "no value"). Critically, never serialise a
  // `undefined` into a Firestore payload — see `lib/utils/sanitize.ts`.
  company?: string | null;
  phone: string;
  /**
   * NIK (individual) or NPWP (business) — one field because a customer is one
   * or the other, never both. Text, since a leading zero counts and NPWP is
   * conventionally punctuated.
   */
  taxId?: string;
  address?: string;
  email?: string | null;
  isActive: boolean; // soft-delete flag — false = deactivated, hidden from POS picker
  createdAt: number;
  updatedAt: number;
}

// --- Transactions ---
export type PaymentMethod = 'cash' | 'qris' | 'transfer';
export type FulfillmentType = 'now' | 'later';
export type PaymentStatus =
  | 'paid'
  | 'pending_delivery'
  | 'pending_payment'  // piutang, not yet due
  | 'overdue'          // piutang, past due date
  | 'partially_returned'
  | 'returned'
  | 'cancelled';
export type DiscountType = 'percent' | 'nominal' | null;

export type PromoKind = 'double_discount' | 'bundle' | 'bogo';

/** A deal on one event. `config` per kind — see migration 0035. */
export interface PromoItem {
  productId: string;
  /** Units of this product in one bundle. 1 for bogo. */
  qty: number;
  /** Bogo only: buy this many… */
  buyQty?: number;
  /** …get this many free. */
  getQty?: number;
}

export interface EventPromo {
  id: string;
  eventId: string;
  kind: PromoKind;
  name: string;
  isActive: boolean;
  /** double_discount: {steps:[30,5]}; bundle: {bundle_price}; bogo: {} (rules live on items) */
  config: Record<string, unknown>;
  sort: number;
  /** Bundle components (qty each) or bogo targets (each with its own buy/get). */
  items: PromoItem[];
}

/** What create_transaction() recorded about one applied promo. */
export interface AppliedPromo {
  promoId: string;
  kind: PromoKind;
  name: string;
  amount: number;
  times?: number;
  free?: number;
  steps?: number[];
}

export interface TransactionItem {
  productId: string;
  /** Which batch this line came out of. Absent on invoices predating lots. */
  allocationId?: string;
  name: string; // denormalized for invoice stability
  price: number;
  qty: number;
  returnedQty: number;
  subtotal: number;
  // Snapshotted like name and price, for the same reason: the invoice has to
  // reprint identically forever, and the allocation may since have changed.
  lotNumber?: string;
  expiryDate?: number;
  unit?: string;
  /** Promo money taken off this line (bundle share or free units). */
  promoDiscount: number;
  promoId?: string;
}

export interface AppTransaction {
  id: string;
  invoiceNumber: string; // INV-YYYYMMDD-NNNN
  eventId: string;
  customerId?: string;
  customerSnapshot?: Pick<Customer, 'name' | 'phone' | 'company' | 'email'>;
  items: TransactionItem[];
  subtotal: number;
  discountType: DiscountType;
  discountValue: number; // 0..100 (percent) or nominal
  discountAmount: number;
  taxEnabled: boolean;
  taxRate: number; // 11 for PPN 11%
  taxAmount: number;
  /** Every promo on the sale: line promos plus the double discount. */
  promoDiscount: number;
  appliedPromos: AppliedPromo[];
  grandTotal: number;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  fulfillment: FulfillmentType;
  fulfilledAt?: number;
  /** Change returned to customer at sale time (overpayment). For tempo
   *  transactions this is 0 unless a DP was overpaid. */
  changeAmount: number;
  cashierId: string;
  cashierName?: string;
  notes?: string;
  // ── Void (batch 2) ── set when paymentStatus === 'cancelled' via void_transaction
  voidedAt?: number;
  voidedByName?: string;
  voidReason?: string;
  voidApprovedByName?: string;
  // ── Receivables (piutang) — added 2026-07-16 ──
  /** 'lunas' = full paid at sale; 'tempo' = piutang. Defaults to 'lunas' for legacy txns. */
  paymentTerms: PaymentTerms;
  /** Due date (epoch ms). Only set when paymentTerms='tempo'. */
  dueDate?: number;
  /** Running total of payments received. For lunas this equals grandTotal
   *  right after sale. For tempo this is the DP at sale time, then grows
   *  as `payments` are recorded against this transaction.
   *  `outstandingAmount = grandTotal - paidAmount`. */
  paidAmount: number;
  outstandingAmount: number;
  /** Server-set fields (Phase 6 — Cloud Function) */
  pdfUrl?: string;
  pdfGeneratedAt?: number;
  pdfGenerationError?: string;
  openclawStatus?: 'sent' | 'failed' | 'skipped' | 'pending';
  openclawAttempts?: number;
  openclawLastError?: string;
  openclawSentAt?: number;
  fulfilledBy?: string;
  createdAt: number;
  updatedAt: number;
}

// --- Returns ---
export type RefundMethod = 'cash' | 'qris' | 'transfer' | 'original';

export interface ReturnItem {
  productId: string;
  name: string;
  qty: number;
  unitPrice: number;
  subtotal: number;
  // Snapshotted from the line being returned, so a credit note names the same
  // batch the invoice did.
  lotNumber?: string;
  expiryDate?: number;
  unit?: string;
}

export interface AppReturn {
  id: string;
  transactionId: string;
  invoiceNumber: string;
  eventId: string;
  items: ReturnItem[];
  refundMethod: RefundMethod;
  refundAmount: number;
  reason?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: number;
}

// --- Receivables / Piutang ---
// Payment terms for a transaction. "Lunas" = full paid at sale time.
// "Tempo" = piutang — customer takes the goods, pays by `dueDate`.
// Optional `downPayment` is the DP collected at sale time.
export type PaymentTerms = 'lunas' | 'tempo';

export interface ReceivablePayment {
  id: string;
  transactionId: string;
  customerId: string;
  eventId: string;
  amount: number;                 // amount paid in this payment (positive)
  paymentMethod: PaymentMethod;
  paidAt: number;                 // epoch ms
  receivedBy: string;             // cashier uid
  receivedByName?: string;
  notes?: string;
  createdAt: number;
  updatedAt: number;
}

export function derivePaymentStatus(
  tx: Pick<AppTransaction, 'paymentTerms' | 'paidAmount' | 'outstandingAmount' | 'dueDate'>,
  now: number = Date.now(),
): PaymentStatus {
  // The other statuses (cancelled, partially_returned, returned) are
  // managed elsewhere; this function only resolves the open-state
  // payment statuses.
  if (tx.outstandingAmount <= 0) return 'paid';
  if (tx.paymentTerms === 'lunas' && tx.outstandingAmount > 0) {
    // Edge case: lunas but somehow outstanding > 0 (partial return)
    return 'partially_returned';
  }
  if (tx.dueDate !== undefined && now > tx.dueDate) return 'overdue';
  return 'pending_payment';
}

// --- Transaction Drafts (per-cashier, in-flight carts) ---
// A draft is a half-built transaction the cashier parked so they can
// serve another customer. Drafts NEVER touch inventory, stock allocations,
// event counters, or invoice numbers — they're pure UI state persisted
// to Firestore so a cashier can resume on a different device or after
// a page refresh.
//
// SECURITY: Strict per-cashier isolation. Server-side rules (see
// firestore.rules: `match /transactionDrafts/{draftId}`) reject any
// read/write/delete from a different auth uid. This prevents:
//   - Cashier A reading Cashier B's drafts
//   - Cashier A writing to Cashier B's draft
//   - An owner overwriting a cashier's draft (by accident or otherwise)
//   - A manager pulling a "secret" draft off the floor
export interface TransactionDraft {
  id: string;
  /** Owning cashier. Server-side: cannot be reassigned. */
  cashierId: string;
  cashierName: string;
  /** Event the draft was created against. Informational only —
   *  the draft is loaded against the cashier's current active shift event. */
  eventId: string;
  eventName: string;
  eventCode: string;
  /** Human-readable label shown in the drafts list. Default = customer
   *  name or "Draft @ HH:MM". Cashier can override on save. */
  label: string;
  items: TransactionItem[];
  subtotal: number;
  discountType: DiscountType;
  discountValue: number;
  discountAmount: number;
  taxEnabled: boolean;
  taxRate: number;
  taxAmount: number;
  grandTotal: number;
  fulfillment: FulfillmentType;
  customerId?: string | null;
  customerSnapshot?: Pick<Customer, 'name' | 'phone' | 'company' | 'email'> | null;
  notes?: string | null;
  createdAt: number;
  updatedAt: number;
}

// --- CompanyInfo (single doc) ---

/**
 * WhatsApp Cloud API (official Meta) config — the supported gateway.
 *
 * NOTE there is no access token here, on purpose. `companyInfo` is readable
 * by any staff member (see firestore.rules), which means every cashier's
 * browser downloads this document. A Meta permanent token can send messages
 * billed to the business, so it is held in Cloud Functions Secret Manager as
 * WHATSAPP_ACCESS_TOKEN and never travels to the client.
 *
 * Takes priority over n8n, Hermes and OpenClaw when enabled.
 */
export interface WhatsAppCloudConfig {
  /** Phone Number ID from WhatsApp Manager — not the phone number itself. */
  phoneNumberId: string;
  /** Name of the approved message template, e.g. `invoice_ready`. */
  templateName: string;
  /** Template language code, e.g. `id`. */
  templateLanguage: string;
  /**
   * How the invoice reaches the customer.
   *   'link' — a button to the PIN-gated invoice page (template `templateName`)
   *   'pdf'  — the invoice attached as a document, nothing to unlock
   *            (template `pdfTemplateName`)
   * Each shape needs its own template approved by Meta, which is why both
   * names are kept: preparing one must not break the one already approved.
   */
  deliveryMode?: 'link' | 'pdf';
  /** Name of the approved document-header template, e.g. `invoice_pdf`. */
  pdfTemplateName?: string;
  /**
   * Language of the PDF template. Separate from `templateLanguage` because the
   * two templates are approved independently and need not share a language —
   * one field for both meant switching mode could send a code Meta had no
   * template for.
   */
  pdfTemplateLanguage?: string;
  enabled: boolean;
}

export interface OpenClawConfig {
  webhookUrl: string;
  apiKey?: string;
  enabled: boolean;
}

/**
 * Hermes (Baileys-based WhatsApp gateway) config.
 * Self-hosted on user's own VPS — replaces OpenClaw for cost / control.
 * If `hermes` is enabled, dispatch goes to Hermes and OpenClaw is ignored.
 * Both configs coexist for zero-downtime migration.
 */
export interface HermesConfig {
  webhookUrl: string;
  apiKey?: string;
  /**
   * Optional shared secret for HMAC-SHA256 signature verification.
   * If set, dispatch includes `X-Ophth-Signature: sha256=<hex>` header.
   */
  hmacSecret?: string;
  enabled: boolean;
  /** Optional session identifier for multi-tenant Hermes deployments. */
  apiKeyId?: string;
}

/**
 * n8n workflow orchestrator config.
 * n8n receives the webhook from Cloud Functions and dispatches to Baileys,
 * with retry logic, message formatting, and delivery logging.
 * Takes priority over Hermes > OpenClaw when enabled.
 */
export interface N8nConfig {
  webhookUrl: string;
  hmacSecret?: string;
  enabled: boolean;
}

export interface CompanyInfo {
  id: 'singleton';
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  logoUrl?: string;
  npwp?: string;
  /**
   * Domain used to build customer-facing links (the public invoice page
   * sent over WhatsApp). No scheme, no trailing slash — e.g.
   * `invoice.mycompany.com`. Leave empty to fall back to the Firebase
   * Hosting site. Read by the Cloud Functions when composing
   * `publicInvoiceLink`.
   */
  customDomain?: string;
  defaultTaxRate: number; // 11
  defaultTaxEnabled: boolean;
  invoiceCounter: number; // per-event, but we keep global for fallback
  paymentMethods: PaymentMethod[];
  /** Official Meta Cloud API — highest priority when enabled. */
  whatsappCloud?: WhatsAppCloudConfig;
  /** Legacy OpenClaw config — kept for backward compat. */
  openclaw: OpenClawConfig;
  /** Newer Hermes config — preferred if enabled. */
  hermes?: HermesConfig;
  /** n8n workflow orchestrator — preferred over Hermes/OpenClaw when enabled. */
  n8n?: N8nConfig;
  createdAt: number;
  updatedAt: number;
}

// --- Audit log ---
// The audit entry type lives in `lib/api/auditLogs.ts` as `AuditLogEntry`,
// next to the reader that maps the Firestore document. It is NOT declared
// here: writers disagree on the actor field (the Cloud Functions write
// `createdBy`, the SPA API modules write `userId`/`userEmail`), and the
// reader normalizes both. A single interface here would have to lie about
// which of those is present.
